"use client"

import { useState } from "react"
import { useTranslations } from "next-intl"
import { ArrowRight } from "lucide-react"
import { GlassCard } from "@/components/glass-card"
import { Slider } from "@/components/ui/slider"
import { LoginButton } from "@/app/[locale]/(auth)/signin/_components/login-button"
import { Link } from "@/i18n/navigation"
import { cn } from "@/lib/utils"
import { formatCurrencyCompact } from "@/lib/utils/currency"
import { BUTTON_STYLES, PILL_STYLES, BOX_STYLES, SLIDER_STYLES, paletteColor, tint } from "./hero-styles"
import { DONATION_SHARE } from "@donista/shared/constants/business-rules"

type Locale = "de" | "en"

const SLIDER_MIN = 10
const SLIDER_MAX = 500

// Locked Design Lab choices (formerly per-element style toggles).
const SLIDER_STYLE = SLIDER_STYLES.filledThumb!
const BADGE_STYLE = PILL_STYLES.soft!
const AMOUNT_BOX = BOX_STYLES.lift!
const START_STYLE = BUTTON_STYLES.lift!
const SLIDER_ACCENT = paletteColor("logoRed")

/**
 * Realistic placeholder commission rate (fraction of spend) for the Ø Spende
 * estimate, shown as a labeled estimate ("Schätzung"/"estimate"). A real
 * platform-wide average is blocked on the Digidip ingestion bug (€ amounts
 * mislabeled as percentages) tracked in #817; once that is fixed and re-synced,
 * a live average endpoint can replace this constant. ~6 % matches the platform's
 * own expected figure; ×0.8 donation share → ~4.8 % shown.
 */
const ESTIMATED_AVG_COMMISSION_RATE = 0.06

export function DonationCalculator({ locale }: { locale: Locale }) {
  const t = useTranslations("start.donationCalculator")
  const [spend, setSpend] = useState(100)
  const [causeIndex, setCauseIndex] = useState(0)

  const rate = ESTIMATED_AVG_COMMISSION_RATE
  const causesRaw = t.raw("causes")
  const causes = Array.isArray(causesRaw) ? (causesRaw as string[]) : []

  const startBtn = (
    <LoginButton size="lg" className={cn("mt-4 h-12 w-full rounded-xl text-base", START_STYLE)}>
      {t("cta")}
    </LoginButton>
  )

  const monthly = spend * rate * DONATION_SHARE
  const yearly = monthly * 12
  const ratePct = new Intl.NumberFormat(locale === "de" ? "de-DE" : "en-US", {
    maximumFractionDigits: 1,
  }).format(rate * DONATION_SHARE * 100)

  return (
    <GlassCard className="mx-auto w-full max-w-lg">
      {/* Spend — heading + amount on one row */}
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-sm font-semibold text-muted-foreground">{t("heading")}</h2>
        <span className="shrink-0">
          <span className="text-3xl font-extrabold tracking-tight">{spend}</span>
          <span className="ml-1 text-xs font-semibold text-muted-foreground">{t("perMonth")}</span>
        </span>
      </div>

      <Slider
        value={[spend]}
        min={SLIDER_MIN}
        max={SLIDER_MAX}
        step={10}
        onValueChange={(v) => setSpend(v[0] ?? SLIDER_MIN)}
        aria-label={t("heading")}
        className={cn("mt-3", SLIDER_STYLE)}
        style={tint(SLIDER_ACCENT)}
      />

      {/* Cause — tight single wrap-row of small chips */}
      <p className="mt-5 text-sm font-medium text-muted-foreground">{t("causeLabel")}</p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {causes.map((label, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setCauseIndex(i)}
            className={cn(
              "rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors",
              i === causeIndex ? BADGE_STYLE : "border-border hover:border-primary",
            )}
          >
            {label}
          </button>
        ))}
        <Link href="/projects" className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/5 px-2.5 py-1 text-xs font-semibold text-primary transition-colors hover:bg-primary/10">
          {t("ctaLink")}
          <ArrowRight className="h-3 w-3" />
        </Link>
      </div>

      {/* Result — horizontal row of two compact figures */}
      <div className="mt-5 grid grid-cols-2 gap-3">
        <div className={cn("rounded-xl px-4 py-3 text-center", AMOUNT_BOX.box)}>
          <span className={cn("block text-2xl font-extrabold tracking-tight", AMOUNT_BOX.figure)}>{formatCurrencyCompact(monthly, locale)}</span>
          <span className="text-xs font-medium text-muted-foreground">{t("perMonthShort")}</span>
        </div>
        <div className={cn("rounded-xl px-4 py-3 text-center", AMOUNT_BOX.box)}>
          <span className={cn("block text-2xl font-extrabold tracking-tight", AMOUNT_BOX.figure)}>{formatCurrencyCompact(yearly, locale)}</span>
          <span className="text-xs font-medium text-muted-foreground">{t("perYearShort")}</span>
        </div>
      </div>

      {startBtn}

      <p className="mt-3 text-center text-xs text-muted-foreground/70">
        {t("rateLabelReal")}: {ratePct} % · {t("footnote")}
      </p>
    </GlassCard>
  )
}
